import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import {
    ReferenceManyField,
    ShowBase,
    TabbedShowLayout,
    useShowContext,
} from 'react-admin';
import { AntennaActivityLog } from '@app/activity/AntennaActivityLog';
import { AnimationIcon } from '@app/animation/AnimationIcon';
import { AntennaSummary } from '@wwf/sdk';
import { SupplyIcon } from '@app/supply/SupplyIcon';
import { AntennaAside } from './AntennaAside';
import { AntennaAnimationInventory } from './AntennaAnimationInventory';
import { AntennaAvatar } from './AntennaAvatar';
import { AntennaSupplyInventory } from './AntennaSupplyInventory';
import { MemberListLayout } from '@app/member/list/MemberListLayout';
import { MissionListLayout } from '@app/mission/list/MissionListLayout';
import { LabelValueRow } from '@app/misc/LabelValueRow';
import { MediaGridLayout } from '@app/media/MediaGridLayout';
import { useResource } from '@app/misc/useResource';
import { AntennaPOIMap } from './AntennaPOIMap';
import { ScrollableTabbedShowLayoutTabs } from '@app/misc/ScrollableTabbedShowLayoutTabs';

export const AntennaShow = () => (
    <ShowBase resource="antenna">
        <AntennaShowContent />
    </ShowBase>
);

const AntennaShowContent = () => {
    const { record, isPending } = useShowContext<AntennaSummary>();

    if (isPending || !record) return null;

    return (
        <Grid container spacing={2} mt={1} rowGap={4}>
            <Grid item xs={12} md={9} sx={{ minWidth: 0 }}>
                <Box
                    flex="1"
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{ minWidth: 0, width: '100%' }}
                >
                    <InfoCard record={record} />
                    <TabCard record={record} />
                </Box>
            </Grid>
            <Grid item md={3}>
                <AntennaAside />
            </Grid>
        </Grid>
    );
};

const InfoCard = ({ record }: { record: AntennaSummary }) => {
    const { data: coverSrc } = useResource(record?.cover?.id);
    return (
        <Card>
            <CardContent>
                <Box display="flex">
                    <Box
                        sx={{
                            height: 200,
                            maxWidth: 500,
                            flexShrink: 0,
                            borderRadius: 2,
                            overflow: 'hidden',
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {coverSrc ? (
                            <Box
                                component="img"
                                src={coverSrc}
                                alt={record.name ?? ''}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                        ) : (
                            <AntennaAvatar variant="xl" clickable />
                        )}
                    </Box>

                    <Box ml={2} flex="1">
                        <Typography variant="h5">{record.name}</Typography>
                        <LabelValueRow
                            label="Description"
                            value={record.description}
                        />
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const TabCard = ({ record }: { record: AntennaSummary }) => {
    return (
        <Card sx={{ minWidth: 0, maxWidth: '100%' }}>
            <CardContent sx={{ minWidth: 0 }}>
                <TabbedShowLayout
                    tabs={<ScrollableTabbedShowLayoutTabs />}
                    sx={{
                        minWidth: 0,
                        maxWidth: '100%',
                        width: '100%',
                        '& .RaTabbedShowLayout-content': { p: 0, minWidth: 0 },
                    }}
                >
                    <TabbedShowLayout.Tab label="Activity">
                        <AntennaActivityLog
                            antennaId={record.id}
                            context="antenna"
                        />
                    </TabbedShowLayout.Tab>
                    <TabbedShowLayout.Tab
                        label={
                            !record.nbMembers
                                ? 'No Member'
                                : record.nbMembers === 1
                                  ? '1 Member'
                                  : `${record.nbMembers} Members`
                        }
                        path="members"
                    >
                        <ReferenceManyField
                            reference="member"
                            target="antennaId"
                            sort={{
                                field: 'lastName',
                                order: 'ASC',
                            }}
                        >
                            <MemberListLayout
                                antennaId={record.id}
                                disableSaveQuery
                            />
                        </ReferenceManyField>
                    </TabbedShowLayout.Tab>
                    <TabbedShowLayout.Tab
                        label={
                            !record.nbMissions
                                ? 'No Mission'
                                : record.nbMissions === 1
                                  ? '1 Mission'
                                  : `${record.nbMissions} Missions`
                        }
                        path="missions"
                    >
                        <ReferenceManyField
                            reference="mission"
                            target="antennaId"
                            sort={{ field: 'name', order: 'ASC' }}
                        >
                            <MissionListLayout
                                antennaId={record.id}
                                disableSaveQuery
                            />
                        </ReferenceManyField>
                    </TabbedShowLayout.Tab>
                    <TabbedShowLayout.Tab label={'POI Map'} path="">
                        <AntennaPOIMap antennaId={record.id} />
                    </TabbedShowLayout.Tab>
                    <TabbedShowLayout.Tab
                        label={
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                            >
                                Animation inventory
                                <AnimationIcon />
                            </Stack>
                        }
                        path="animation-inventory"
                    >
                        <AntennaAnimationInventory
                            antennaId={record.id}
                            antennaName={record.name}
                        />
                    </TabbedShowLayout.Tab>
                    <TabbedShowLayout.Tab
                        label={
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.5}
                            >
                                Supply inventory
                                <SupplyIcon />
                            </Stack>
                        }
                        path="supply-inventory"
                    >
                        <AntennaSupplyInventory
                            antennaId={record.id}
                            antennaName={record.name}
                        />
                    </TabbedShowLayout.Tab>
                    <TabbedShowLayout.Tab
                        label={
                            !record.nbMedias
                                ? 'No Medias'
                                : record.nbMedias === 1
                                  ? '1 Media'
                                  : `${record.nbMedias} Medias`
                        }
                        path="medias"
                    >
                        <ReferenceManyField
                            target="antennaId"
                            reference="missionMedia"
                            sort={{ field: 'createdAt', order: 'DESC' }}
                        >
                            <MediaGridLayout />
                        </ReferenceManyField>
                    </TabbedShowLayout.Tab>
                </TabbedShowLayout>
            </CardContent>
        </Card>
    );
};
